import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { MdEdit, MdDelete, MdVisibility, MdClose, MdSearch } from 'react-icons/md';
import ImageCropper from '../components/ImageCropper';
import WebPreviewModal from '../components/WebPreviewModal';
import DuplicateMatchesModal from '../components/DuplicateMatchesModal';
import Pagination from '../components/Pagination';
import usePagination from '../hooks/usePagination';
import { useAuth } from '../context/AuthContext';
import { formatDateTime, formatINR, isAllIndiaState, isInternationalState, statesMatch, ALL_INDIA_STATE, INTERNATIONAL_STATE, displayState } from '../utils/format';

function SearchableSelect({ label, value, options, onChange, placeholder = "Search...", searchKey = "slug" }) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filtered = options.filter(opt =>
    (opt.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (opt[searchKey] || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="form-group">
      <label>{label}</label>
      <div className="searchable-select">
        <div className="select-box" onClick={() => setIsOpen(!isOpen)}>
          {(typeof value === 'string' ? displayState(value) : value) || `Select ${label}`}
        </div>
        {isOpen && (
          <div className="select-dropdown">
            <input
              autoFocus
              placeholder={placeholder}
              value={search}
              onChange={e => setSearch(e.target.value)}
              onClick={e => e.stopPropagation()}
            />
            <div className="options-list">
              {filtered.map(opt => (
                <div
                  key={opt.id || opt.name}
                  className="option-item"
                  onClick={() => {
                    onChange(opt.name);
                    setIsOpen(false);
                    setSearch('');
                  }}
                >
                  <span>{displayState(opt.name)}</span>
                  {opt[searchKey] && <span className="text-muted text-xs">({opt[searchKey]})</span>}
                </div>
              ))}
              {filtered.length === 0 && <div className="option-item text-muted">No results found</div>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ContributorSelect({ value, contributors, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedContributor = contributors.find(c => String(c.id) === String(value));
  const displayText = selectedContributor ? selectedContributor.name : 'Select Contributor';

  const filtered = contributors.filter(c =>
    (c.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (c.phone || '').includes(search) ||
    (c.email || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div ref={dropdownRef} className="contributor-filter-wrapper" style={{ position: 'relative', display: 'inline-block' }}>
      {/* Hidden native select for accessibility and test automation */}
      <select
        id="contributor-filter-select"
        aria-label="Contributor"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          opacity: 0,
          pointerEvents: isOpen ? 'none' : 'auto',
          cursor: 'pointer',
          zIndex: 1,
        }}
      >
        <option value="">Select Contributor</option>
        {contributors.map(c => (
          <option key={c.id} value={c.id}>{c.name}</option>
        ))}
      </select>

      {/* Styled visible trigger button matching filter-bar select */}
      <div
        className="filter-select-trigger"
        onClick={() => setIsOpen(!isOpen)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px',
          padding: '9px 12px',
          border: '1.5px solid var(--border)',
          borderRadius: '8px',
          background: 'var(--bg-card, #ffffff)',
          color: value ? 'var(--text)' : 'var(--text-secondary, #64748b)',
          fontSize: '13px',
          minWidth: '180px',
          maxWidth: '240px',
          cursor: 'pointer',
          userSelect: 'none',
          boxSizing: 'border-box',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {displayText}
        </span>
        <span style={{ fontSize: '10px', color: 'var(--text-secondary, #94a3b8)', marginLeft: '4px' }}>▼</span>
      </div>

      {/* Custom searchable dropdown when open */}
      {isOpen && (
        <div
          className="contributor-dropdown-menu"
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            zIndex: 1050,
            minWidth: '220px',
            width: 'max-content',
            maxWidth: '300px',
            background: 'var(--bg-card, #ffffff)',
            border: '1px solid var(--border)',
            borderRadius: '8px',
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
            padding: '6px',
            boxSizing: 'border-box',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ padding: '4px', borderBottom: '1px solid var(--border)', marginBottom: '4px' }}>
            <input
              type="text"
              autoFocus
              placeholder="Search contributor..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                width: '100%',
                padding: '6px 10px',
                fontSize: '12px',
                border: '1px solid var(--border)',
                borderRadius: '6px',
                background: 'var(--bg-main, #f8fafc)',
                color: 'var(--text)',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>
          <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
            <div
              onClick={() => {
                onChange('');
                setIsOpen(false);
                setSearch('');
              }}
              style={{
                padding: '8px 10px',
                fontSize: '12px',
                cursor: 'pointer',
                borderRadius: '6px',
                color: !value ? '#4F46E5' : 'var(--text)',
                fontWeight: !value ? '600' : 'normal',
                background: !value ? 'rgba(79, 70, 229, 0.08)' : 'transparent',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(79, 70, 229, 0.08)'}
              onMouseLeave={(e) => e.currentTarget.style.background = !value ? 'rgba(79, 70, 229, 0.08)' : 'transparent'}
            >
              Select Contributor
            </div>
            {filtered.map(c => {
              const isSelected = String(c.id) === String(value);
              return (
                <div
                  key={c.id}
                  onClick={() => {
                    onChange(String(c.id));
                    setIsOpen(false);
                    setSearch('');
                  }}
                  style={{
                    padding: '8px 10px',
                    fontSize: '12px',
                    cursor: 'pointer',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    color: isSelected ? '#4F46E5' : 'var(--text)',
                    fontWeight: isSelected ? '600' : 'normal',
                    background: isSelected ? 'rgba(79, 70, 229, 0.08)' : 'transparent',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(79, 70, 229, 0.08)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = isSelected ? 'rgba(79, 70, 229, 0.08)' : 'transparent'}
                >
                  <span>{c.name}</span>
                  {c.phone && <span style={{ fontSize: '11px', color: 'var(--text-secondary, #94a3b8)', marginLeft: '8px' }}>{c.phone}</span>}
                </div>
              );
            })}
            {filtered.length === 0 && (
              <div style={{ padding: '10px', fontSize: '12px', color: 'var(--text-secondary, #94a3b8)', textAlign: 'center' }}>
                No contributors found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Articles() {
  const { admin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const isTopEarning = queryParams.get('top_earning') === 'true';
  const topContributorId = queryParams.get('contributor_id');

  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [reporters, setReporters] = useState([]);
  const [contributors, setContributors] = useState([]);
  const [articles, setArticles] = useState([]);
  const [tab, setTab] = useState('all');
  const [filters, setFilters] = useState({ category: '', city: '', status: admin?.role === 'reporter' ? 'draft' : '', admin_id: '', author_type: '', contributor_id: '', zone_only: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const { page, limit, setPage, setLimit } = usePagination(15);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [loading, setLoading] = useState(true);

  const stateMapping = {
    'ఆంధ్రప్రదేశ్': 'Andhra Pradesh',
    'తెలంగాణ': 'Telangana',
    'జాతీయం': 'All India',
    'అంతర్జాతీయం': 'International',
    'అంతర్జాతీయ': 'International',
    'International': 'International',
    // Legacy "ఆల్/అల్ ఇండియా" spellings — tolerated for old rows, not written going forward.
    'ఆల్ ఇండియా': 'All India',
    'ఆల్ ఇండియా ': 'All India',
    'అల్ ఇండియా': 'All India',
    'అల్ ఇండియా ': 'All India',
    'ఆంధ్ర ప్రదేశ్': 'Andhra Pradesh',
    'Telangana': 'Telangana',
    'Andhra Pradesh': 'Andhra Pradesh'
  };

  // Edit modal
  const [editModal, setEditModal] = useState(null);
  const [editForm, setEditForm] = useState({});

  // View modal
  const [viewModal, setViewModal] = useState(null);

  // Delete confirmation modal
  const [deleteModal, setDeleteModal] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Duplicate-matches modal — holds the id of the flagged article being reviewed
  const [duplicateModal, setDuplicateModal] = useState(null);

  const fetchArticles = useCallback(async () => {
    setLoading(true);
    window.scrollTo(0, 0);
    try {
      if (isTopEarning && topContributorId) {
        const res = await api.get(`/admin/contributors/${topContributorId}/top-earning-articles`);
        setArticles(res.data.data);
        setPagination({ page: 1, pages: 1, total: res.data.data.length });
        setLoading(false);
        return;
      }

      const params = { page, limit };
      if (tab === 'archive') {
        const res = await api.get('/admin/articles/archive', { params });
        setArticles(res.data.data);
        setPagination(res.data.pagination);
      } else {
        if (filters.category) params.category = filters.category;
        if (filters.city) params.city = filters.city;
        if (filters.author_type) params.author_type = filters.author_type;
        if (filters.admin_id) params.admin_id = filters.admin_id;
        if (filters.contributor_id) params.contributor_id = filters.contributor_id;
        if (filters.zone_only) params.zone_only = filters.zone_only;
        params.status = filters.status || 'published';
        if (searchQuery.trim()) params.search = searchQuery.trim();
        const res = await api.get('/admin/articles', { params });
        setArticles(res.data.data);
        setPagination(res.data.pagination);
      }
    } catch (err) {
      toast.error('Failed to load articles');
    } finally {
      setLoading(false);
    }
  }, [tab, filters, searchQuery, isTopEarning, topContributorId, page, limit]);

  // Fetch instantly on page/limit change (page clicks shouldn't feel debounced).
  useEffect(() => {
    fetchArticles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit]);

  // Reset to page 1 (debounced) when the search/tab/filters actually change —
  // but not on first mount, so a bookmarked/refreshed ?page=3 URL sticks.
  // If already on page 1, setPage(1) wouldn't change anything, so fetch directly.
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) { isFirstRender.current = false; return; }
    const delayDebounceFn = setTimeout(() => {
      if (page === 1) fetchArticles(); else setPage(1);
    }, 500);

    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, tab, filters]);

  useEffect(() => {
    api.get('/categories').then(res => setCategories(res.data.data)).catch(() => { });
    api.get('/cities').then(res => setCities(res.data.data)).catch(() => { });
    if (admin?.role === 'admin') {
      api.get('/admin/admins').then(res => {
        setReporters(res.data.data);
      }).catch(() => { });
      api.get('/admin/contributors', { params: { limit: 1000, sortBy: 'name', sortOrder: 'ASC' } }).then(res => {
        setContributors(res.data?.data || []);
      }).catch(() => { });
    }
  }, [admin]);

  const handleAuthorChange = (e) => {
    let val = e.target.value;
    if (val === 'super_admin' || val === 'Super Admin') {
      const superAdmin = reporters.find(r => r.role === 'admin' || r.name.toLowerCase().includes('super admin'));
      setFilters(f => ({
        ...f,
        author_type: 'super_admin',
        admin_id: superAdmin ? String(superAdmin.id) : '1',
        contributor_id: '',
      }));
    } else if (val === 'contributors' || val === 'Contributors') {
      setFilters(f => ({
        ...f,
        author_type: 'contributors',
        admin_id: '',
        contributor_id: '',
      }));
    } else if (val.startsWith('reporter_')) {
      const repId = val.replace('reporter_', '');
      setFilters(f => ({
        ...f,
        author_type: 'reporter',
        admin_id: repId,
        contributor_id: '',
      }));
    } else if (!val || val === 'All Users' || val === 'all') {
      setFilters(f => ({
        ...f,
        author_type: '',
        admin_id: '',
        contributor_id: '',
      }));
    } else {
      const matched = reporters.find(r => String(r.id) === String(val));
      if (matched && (matched.role === 'admin' || matched.name.toLowerCase().includes('super admin'))) {
        setFilters(f => ({
          ...f,
          author_type: 'super_admin',
          admin_id: String(matched.id),
          contributor_id: '',
        }));
      } else {
        setFilters(f => ({
          ...f,
          author_type: val ? 'reporter' : '',
          admin_id: val,
          contributor_id: '',
        }));
      }
    }
  };

  const filteredArticles = articles;

  const handleAiGenerate = async (type, extraData = null) => {
    if (type === 'slug' && !editForm.title) {
      return toast.error('Please enter a title first');
    }
    if (type === 'transliterate' && !extraData) {
      return toast.error('Please enter some text to convert');
    }

    setLoading(true);
    try {
      const payload = { type };
      if (type === 'slug') payload.title = editForm.title;
      if (type === 'transliterate') payload.text = extraData;

      const res = await api.post('/admin/ai/generate', payload);
      if (res.data.success) {
        if (type === 'slug') setEditForm(f => ({ ...f, slug: res.data.data }));
        if (type === 'transliterate') {
          return res.data.data;
        }
        toast.success('Generated');
      }
    } catch (err) {
      toast.error('AI operation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleTransliterateEdit = async (field) => {
    const text = editForm[field];
    if (!text) return toast.error(`Please type something in ${field} first`);
    const result = await handleAiGenerate('transliterate', text);
    if (result) {
      const limit = field === 'title' ? 75 : field === 'summary' ? 400 : result.length;
      setEditForm(f => ({ ...f, [field]: result.slice(0, limit) }));
    }
  };

  const confirmDelete = async () => {
    if (!deleteModal) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/articles/${deleteModal.id}`);
      toast.success('Article deleted');
      setDeleteModal(null);
      fetchArticles();
    } catch (err) {
      toast.error('Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const [editUploading, setEditUploading] = useState(false);
  const [editGalleryUploading, setEditGalleryUploading] = useState(false);
  const [showEditWebPreview, setShowEditWebPreview] = useState(false);
  const [editCropper, setEditCropper] = useState({ active: false, image: null, type: null });

  const [editState, setEditState] = useState('');
  const [editCities, setEditCities] = useState([]);

  const handleEditGalleryFilesUpload = async (files) => {
    if (!files || files.length === 0) return;
    setEditGalleryUploading(true);
    const newUrls = [];
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await api.post('/admin/upload/image', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        if (res.data?.data?.url) {
          newUrls.push(res.data.data.url);
        }
      } catch (err) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    if (newUrls.length > 0) {
      setEditForm(prev => ({
        ...prev,
        images: [...(prev.images || []), ...newUrls]
      }));
      toast.success(`${newUrls.length} gallery image(s) uploaded`);
    }
    setEditGalleryUploading(false);
  };

  const moveEditGalleryImage = (index, direction) => {
    setEditForm(prev => {
      const arr = [...(prev.images || [])];
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= arr.length) return prev;
      const temp = arr[index];
      arr[index] = arr[targetIndex];
      arr[targetIndex] = temp;
      return { ...prev, images: arr };
    });
  };

  const removeEditGalleryImage = (index) => {
    setEditForm(prev => ({
      ...prev,
      images: (prev.images || []).filter((_, i) => i !== index)
    }));
  };

  const handleEditGalleryPasteImage = (e) => {
    const items = e.clipboardData?.items || [];
    const filesToUpload = [];
    for (const it of items) {
      if (it.type && it.type.startsWith('image/')) {
        const file = it.getAsFile();
        if (file) {
          filesToUpload.push(file);
        }
      }
    }
    if (filesToUpload.length > 0) {
      e.preventDefault();
      e.stopPropagation();
      handleEditGalleryFilesUpload(filesToUpload);
    }
  };

  const openEdit = (article) => {
    setEditModal(article);
    const cityObj = cities.find(c => c.name === article.city);
    const rawState = cityObj ? cityObj.state : (article.state || '');
    const initialState = isAllIndiaState(rawState) ? ALL_INDIA_STATE + ' ' : (isInternationalState(rawState) ? INTERNATIONAL_STATE : rawState);
    setEditState(initialState);
    setEditCities(cities.filter(c => c.state && statesMatch(c.state, initialState)));

    setEditForm({
      title: article.title || '',
      summary: article.summary || '',
      category: article.category || '',
      city: article.city || '',
      source_url: article.source_url || '',
      source: article.source || '',
      media_type: article.media_type || 'image',
      image_url: article.image_url || '',
      thumbnail_url: article.thumbnail_url || '',
      video_url: article.video_url || '',
      is_breaking: article.is_breaking || false,
      breaking_state: article.breaking_state || ALL_INDIA_STATE,
      custom_notification_title: article.custom_notification_title || '',
      is_featured: article.is_featured || false,
      zone_only: article.zone_only || false,
      slug: (article.slug || '').split('-').slice(0, -1).join('-'), // Show without ID
      author_name: article.author_name || '',
      images: article.images ? article.images.map(img => img.image_url) : [],
      status: article.status || 'draft',
      manual_thumbnail: false,
    });
  };

  const handleEditFileSelect = (fileOrType, type) => {
    if (typeof fileOrType === 'string') {
      const currentType = fileOrType;
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = currentType === 'video' ? 'video/mp4' : 'image/*';

      // Store reference to prevent garbage collection
      window._tempFileInput = input;

      input.onchange = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        const selectedFile = files[0];
        if (currentType === 'video') {
          uploadEditToServer(selectedFile, currentType);
        } else {
          const reader = new FileReader();
          reader.onload = () => {
            setEditCropper({ active: true, image: reader.result, type: currentType });
          };
          reader.readAsDataURL(selectedFile);
        }
      };
      input.click();
      return;
    }

    const file = fileOrType;
    if (type === 'video') {
      uploadEditToServer(file, type);
    } else {
      if (file && file instanceof Blob) {
        const reader = new FileReader();
        reader.onload = () => {
          setEditCropper({ active: true, image: reader.result, type });
        };
        reader.readAsDataURL(file);
      }
    }
  };

  useEffect(() => {
    const handleGlobalPaste = (e) => {
      if (!editModal) return;
      if (editForm?.media_type !== 'image') return;
      if (editCropper.active || showEditWebPreview) return;

      const items = e.clipboardData?.items || [];
      for (const it of items) {
        if (it.type && it.type.startsWith('image/')) {
          const file = it.getAsFile();
          if (file) {
            e.preventDefault();
            const reader = new FileReader();
            reader.onload = () => {
              setEditCropper({ active: true, image: reader.result, type: 'image' });
            };
            reader.readAsDataURL(file);
            return;
          }
        }
      }
    };

    window.addEventListener('paste', handleGlobalPaste);
    return () => window.removeEventListener('paste', handleGlobalPaste);
  }, [editModal, editForm?.media_type, editCropper.active, showEditWebPreview]);

  const handleEditPasteImage = (e) => {
    e.preventDefault();
  };

  const handleEditCropComplete = async (blob) => {
    const type = editCropper.type;
    setEditCropper({ active: false, image: null, type: null });

    const file = new File([blob], `cropped-${Date.now()}.jpg`, { type: 'image/jpeg' });
    uploadEditToServer(file, type);
  };

  const uploadEditToServer = async (file, type) => {
    const formData = new FormData();
    formData.append('file', file);
    const endpoint = (type === 'image' || type === 'extra_image') ? '/admin/upload/image'
      : type === 'thumbnail' ? '/admin/upload/thumbnail'
        : '/admin/upload/video';

    setEditUploading(true);
    try {
      const res = await api.post(endpoint, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = res.data.data.url;
      const thumbnailUrl = res.data.data.thumbnail_url;

      if (type === 'image') setEditForm(f => ({ ...f, image_url: url }));
      else if (type === 'extra_image') setEditForm(f => ({ ...f, images: [...(f.images || []), url] }));
      else if (type === 'thumbnail') {
        setEditForm(f => ({ ...f, thumbnail_url: url, manual_thumbnail: true }));
      }
      else if (type === 'video') {
        setEditForm(f => {
          const nextForm = { ...f, video_url: url };
          if (thumbnailUrl && !f.manual_thumbnail) {
            nextForm.thumbnail_url = thumbnailUrl;
          }
          return nextForm;
        });
      }
      toast.success(`${type === 'extra_image' ? 'Image' : type} uploaded and resized!`);
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setEditUploading(false);
    }
  };

  const saveEdit = async () => {
    if (!editModal) return;
    try {
      await api.put(`/admin/articles/${editModal.id}`, editForm);
      toast.success('Article updated');
      setEditModal(null);
      fetchArticles();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Update failed');
    }
  };

  const handleEditSubmit = (e) => {
    if (e) e.preventDefault();
    saveEdit();
  };

  const isLive = (article) => !article.is_archived && new Date(article.expires_at) > new Date() && article.status === 'published';

  // Single source of truth for the status badge — desktop table + mobile cards
  // both use this so the AI duplicate-check states render consistently.
  const getStatusBadge = (a) => {
    if (a.status === 'ai_check_pending') {
      return { label: 'AI Check Pending', className: 'badge-info', onClick: null };
    }
    if (a.status === 'ai_check_flagged') {
      return { label: '⚠ Possible Duplicate', className: 'badge-warning', onClick: () => setDuplicateModal(a.id) };
    }
    if (a.status === 'draft') return { label: 'Draft', className: 'badge-category', onClick: null };
    if (a.status === 'scheduled') return { label: 'Scheduled', className: 'badge-category', onClick: null };
    return { label: isLive(a) ? 'Live' : 'Expired', className: isLive(a) ? 'badge-live' : 'badge-expired', onClick: null };
  };

  return (
    <div className="page">

      {!isTopEarning && (
        <div className="page-header">
          <h2>Published Articles</h2>
          <p className="text-muted">Manage all published articles across categories and cities.</p>
        </div>
      )}

      {!isTopEarning && (
        <div className="tabs">
          <button className={`tab ${tab === 'all' ? 'active' : ''}`} onClick={() => setTab('all')}>All Articles</button>
          <button className={`tab ${tab === 'archive' ? 'active' : ''}`} onClick={() => setTab('archive')}>Archive</button>
        </div>
      )}

      {!isTopEarning && tab === 'all' && (
        <>
          <div className="filter-bar">
            <div className="search-input-wrap">
              <MdSearch className="search-icon" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search articles by title..."
                className="search-input"
              />
            </div>
            <select value={filters.category} onChange={(e) => setFilters(f => ({ ...f, category: e.target.value, city: '' }))}>
              <option value="">All Categories</option>
              {categories.map(c => <option key={c.slug} value={c.name}>{c.name}</option>)}
            </select>
            <select value={filters.city} onChange={(e) => setFilters(f => ({ ...f, city: e.target.value }))}>
              <option value="">All Cities</option>
              {cities
                .filter(c => {
                  if (!filters.category) return true;
                  const cat = filters.category.trim();
                  const isStateCategory = ['తెలంగాణ', 'Telangana', 'ఆంధ్రప్రదేశ్', 'ఆంధ్ర ప్రదేశ్', 'Andhra Pradesh'].includes(cat);
                  if (!isStateCategory) return true;
                  return c.state === cat || c.state === stateMapping[cat];
                })
                .map(c => <option key={c.slug} value={c.name}>{c.name}</option>)}
            </select>
            {admin?.role === 'admin' && (
              <select
                id="author-filter-select"
                aria-label="Author"
                value={
                  filters.author_type === 'contributors'
                    ? 'contributors'
                    : filters.author_type === 'super_admin'
                      ? 'super_admin'
                      : filters.admin_id
                        ? `reporter_${filters.admin_id}`
                        : ''
                }
                onChange={handleAuthorChange}
              >
                <option value="">All Users</option>
                <option value="super_admin">Super Admin</option>
                {reporters.filter(r => r.name !== 'Super Admin' && r.role === 'reporter').map(r => (
                  <option key={r.id} value={`reporter_${r.id}`}>{r.name}</option>
                ))}
                <option value="contributors">Contributors</option>
              </select>
            )}
            {admin?.role === 'admin' && filters.author_type === 'contributors' && (
              <ContributorSelect
                value={filters.contributor_id}
                contributors={contributors}
                onChange={(val) => setFilters(f => ({ ...f, contributor_id: val }))}
              />
            )}
            <select value={filters.zone_only} onChange={(e) => setFilters(f => ({ ...f, zone_only: e.target.value }))}>
              <option value="">Zone: All</option>
              <option value="true">Zone Only</option>
              <option value="false">Others (Not Zone)</option>
            </select>
            {admin?.role === 'reporter' ? (
              <select value={filters.status} onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))}>
                <option value="draft">Draft Articles</option>
                <option value="">All Published</option>
                <option value="live">Active (Live) Articles</option>
                <option value="scheduled">Scheduled</option>
                <option value="expired">Expired Articles</option>
                <option value="ai_check_pending">AI Check Pending</option>
                <option value="ai_check_flagged">Possible Duplicates</option>
                <option value="past_12_hours">Past 12 Hours</option>
                <option value="past_24_hours">Past 24 Hours</option>
              </select>
            ) : (
              <select value={filters.status} onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))}>
                <option value="">All Published</option>
                <option value="live">Live (Active)</option>
                <option value="scheduled">Scheduled</option>
                <option value="expired">Expired</option>
                <option value="ai_check_pending">AI Check Pending</option>
                <option value="ai_check_flagged">Possible Duplicates</option>
                <option value="past_12_hours">Past 12 Hours</option>
                <option value="past_24_hours">Past 24 Hours</option>
              </select>
            )}
          </div>

          {(filters.category || (admin?.role === 'admin' && (filters.author_type || filters.admin_id || filters.contributor_id))) && (
            <div style={{ display: 'flex', gap: '10px', marginTop: '12px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
              {admin?.role === 'admin' && (filters.author_type === 'super_admin' || (!filters.author_type && filters.admin_id && reporters.find(r => r.id == filters.admin_id)?.role === 'admin')) && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 12px',
                  background: '#ECF0FF',
                  border: '1.5px solid #C7D2FE',
                  borderRadius: '20px',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#4F46E5',
                  fontFamily: 'Ramabhadra, serif',
                }}>
                  📝 Super Admin
                  <button onClick={() => setFilters(f => ({ ...f, author_type: '', admin_id: '', contributor_id: '' }))} style={{ background: 'none', border: 'none', color: '#4F46E5', cursor: 'pointer', fontSize: '16px', padding: '0', display: 'flex', alignItems: 'center', lineHeight: '1' }}>×</button>
                </div>
              )}
              {admin?.role === 'admin' && (filters.author_type === 'contributors' || filters.contributor_id) && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 12px',
                  background: '#ECF0FF',
                  border: '1.5px solid #C7D2FE',
                  borderRadius: '20px',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#4F46E5',
                  fontFamily: 'Ramabhadra, serif',
                }}>
                  📝 {filters.contributor_id ? (contributors.find(c => String(c.id) === String(filters.contributor_id))?.name || 'Contributor') : 'All Contributors'}
                  <button onClick={() => setFilters(f => ({ ...f, author_type: '', admin_id: '', contributor_id: '' }))} style={{ background: 'none', border: 'none', color: '#4F46E5', cursor: 'pointer', fontSize: '16px', padding: '0', display: 'flex', alignItems: 'center', lineHeight: '1' }}>×</button>
                </div>
              )}
              {admin?.role === 'admin' && filters.author_type === 'reporter' && filters.admin_id && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 12px',
                  background: '#ECF0FF',
                  border: '1.5px solid #C7D2FE',
                  borderRadius: '20px',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#4F46E5',
                  fontFamily: 'Ramabhadra, serif',
                }}>
                  📝 {reporters.find(r => String(r.id) === String(filters.admin_id))?.name}
                  <button onClick={() => setFilters(f => ({ ...f, author_type: '', admin_id: '', contributor_id: '' }))} style={{ background: 'none', border: 'none', color: '#4F46E5', cursor: 'pointer', fontSize: '16px', padding: '0', display: 'flex', alignItems: 'center', lineHeight: '1' }}>×</button>
                </div>
              )}
              {filters.category && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 12px',
                  background: '#FEF3C7',
                  border: '1.5px solid #FCD34D',
                  borderRadius: '20px',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#B45309',
                  fontFamily: 'Ramabhadra, serif',
                }}>
                  📂 {filters.category}
                  <button onClick={() => setFilters(f => ({ ...f, category: '', city: '' }))} style={{ background: 'none', border: 'none', color: '#B45309', cursor: 'pointer', fontSize: '16px', padding: '0', display: 'flex', alignItems: 'center', lineHeight: '1' }}>×</button>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {loading ? <div className="page-loader">Loading...</div> : (
        <>
          {isTopEarning ? (
            <div className="table-wrap">
              <table className="table" style={{ fontSize: 13 }}>
                <thead>
                  <tr><th>#</th><th>Article Title</th><th>Published Date</th><th>Reads</th><th>Earnings (₹)</th></tr>
                </thead>
                <tbody>
                  {articles.map((art, idx) => (
                    <tr key={art.id}>
                      <td>{idx + 1}</td>
                      <td>{art.title}</td>
                      <td>{formatDateTime(art.published_at)}</td>
                      <td>{art.read_count}</td>
                      <td style={{ fontWeight: 600 }}>{formatINR(art.earnings)}</td>
                    </tr>
                  ))}
                  {articles.length === 0 && <tr><td colSpan="5" className="empty-row">No top earning articles found</td></tr>}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th className="hide-mobile">Image</th>
                    <th>Title</th>
                    <th>Author</th>
                    <th className="hide-mobile">Shares</th>
                    <th className="hide-mobile">Views</th>
                    <th className="hide-mobile">Date</th>
                    {filters.status === 'scheduled' && <th>Scheduled Publish</th>}
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredArticles.map(a => {
                    const publishedDate = new Date(a.published_at);
                    const dateStr = publishedDate.toLocaleDateString('en-GB');
                    const timeStr = publishedDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
                    const imageSrc = a.thumbnail_url || a.image_url || '/placeholder.png';
                    const sb = getStatusBadge(a);
                    // Live/Expired is shown as a border around the thumbnail instead of
                    // a status column; the rarer states (AI check, draft, scheduled)
                    // still need their own badge since a border can't carry that much info.
                    const thumbBorderColor = sb.className === 'badge-live' ? '#10B981' : sb.className === 'badge-expired' ? '#DC2626' : 'transparent';

                    return (
                      <tr key={a.id} className={a.is_breaking ? 'row-breaking' : ''}>
                        <td className="hide-mobile" style={{ textAlign: 'center', padding: '8px' }}>
                          <img
                            src={imageSrc}
                            alt={a.title}
                            title={sb.className === 'badge-live' ? 'Live' : sb.className === 'badge-expired' ? 'Expired' : sb.label}
                            style={{ width: '75px', height: '75px', objectFit: 'cover', borderRadius: '8px', border: `3px solid ${thumbBorderColor}` }}
                            onError={(e) => e.target.style.display = 'none'}
                          />
                        </td>
                        <td className="title-cell">
                          {a.is_breaking && (
                            <span className="breaking-tag selected" style={{ marginRight: '8px', fontSize: '11px', display: 'inline-block', padding: '2px 6px', marginBottom: '4px' }}>
                              Breaking
                            </span>
                          )}
                          <span className="article-title">{a.title}</span>
                          <div style={{ fontSize: '12px', marginTop: '6px', display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <span style={{ color: '#EA580C', padding: '6px 10px', background: '#FFEDD5', borderRadius: '6px' }}>
                              {a.category && a.category.trim() !== 'General' ? a.category : displayState(a.city?.replace('_', ' '))}
                            </span>
                            {sb.className !== 'badge-live' && sb.className !== 'badge-expired' && (
                              <span
                                className={`badge ${sb.className}`}
                                onClick={sb.onClick || undefined}
                                style={sb.onClick ? { cursor: 'pointer', textDecoration: 'underline' } : undefined}
                                title={sb.onClick ? 'Click to view matching articles' : undefined}
                              >
                                {sb.label}
                              </span>
                            )}
                          </div>
                        </td>
                        <td style={{ fontSize: '13px', color: 'var(--text)' }}>
                          {(a.contributor?.id || a.creator?.id) ? (
                            <span
                              onClick={(e) => { e.stopPropagation(); navigate(a.contributor?.id ? `/authors/contributor/${a.contributor.id}` : `/authors/admin/${a.creator.id}`); }}
                              title="View author details"
                              style={{ color: '#0369A1', padding: '6px 10px', background: '#E0F2FE', borderRadius: '6px', display: 'inline-block', cursor: 'pointer', textDecoration: 'underline' }}
                            >
                              {a.contributor?.name || a.author_name || a.creator?.name || '-'}
                            </span>
                          ) : (
                            <span style={{ color: '#0369A1', padding: '6px 10px', background: '#E0F2FE', borderRadius: '6px', display: 'inline-block' }}>
                              {a.author_name || '-'}
                            </span>
                          )}
                        </td>
                        <td className="hide-mobile">
                          <span
                            onClick={(e) => { e.stopPropagation(); navigate(`/articles/engagement/${a.id}?tab=shares`); }}
                            title="View Shares engagement"
                            style={{
                              fontSize: '12px',
                              color: '#059669',
                              background: '#ECFDF5',
                              padding: '4px 8px',
                              borderRadius: '6px',
                              fontWeight: '600',
                              cursor: 'pointer',
                              display: 'inline-block',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            ↗️ {a.share_count || 0} Shares
                          </span>
                        </td>
                        <td className="hide-mobile">{a.view_count}</td>
                        <td className="hide-mobile">
                          <div>{dateStr}</div>
                          <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{timeStr}</div>
                        </td>
                        {filters.status === 'scheduled' && (
                          <td>{a.status === 'scheduled' ? formatDateTime(a.published_at) : '-'}</td>
                        )}
                        <td>
                          <div className="action-btns">
                            <button className="btn btn-sm btn-ghost" onClick={() => setViewModal(a)} title="View"><MdVisibility size={16} /> View</button>
                            {(admin?.role !== 'reporter' || a.status === 'draft') && <button className="btn btn-sm btn-ghost" onClick={() => openEdit(a)} title="Edit"><MdEdit size={16} /> Edit</button>}
                            <button className="btn btn-sm btn-danger" onClick={() => setDeleteModal(a)} title="Delete"><MdDelete size={16} /> Delete</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {filteredArticles.length === 0 && (
                    <tr><td colSpan="6" className="empty-row">No articles found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {!isTopEarning && (
            <div className="mobile-cards hide-desktop">
              {filteredArticles.map(a => (
                <div key={a.id} className="mobile-card">
                  <div className="mobile-card-header">
                    {(() => {
                      const sb = getStatusBadge(a);
                      return (
                        <span
                          className={`badge ${sb.className}`}
                          onClick={sb.onClick || undefined}
                          style={sb.onClick ? { cursor: 'pointer', textDecoration: 'underline' } : undefined}
                        >
                          {sb.label}
                        </span>
                      );
                    })()}
                    <span className="badge badge-category">{a.category?.replace('_', ' ')}</span>
                  </div>
                  <h4>{a.title}</h4>
                  <div className="mobile-card-stats">
                    {(a.contributor?.id || a.creator?.id) ? (
                      <span
                        onClick={() => navigate(a.contributor?.id ? `/authors/contributor/${a.contributor.id}` : `/authors/admin/${a.creator.id}`)}
                        style={{ cursor: 'pointer', textDecoration: 'underline', color: '#0369A1' }}
                      >
                        {a.contributor?.name || a.author_name || a.creator?.name}
                      </span>
                    ) : (
                      <span>{a.author_name || '-'}</span>
                    )}
                    <span>{a.share_count || 0} shares</span>
                    <span>{a.view_count} views</span>
                    <span>{a.like_count} likes</span>
                    <span>{a.category && a.category.trim() !== 'General' ? a.category : displayState(a.city?.replace('_', ' '))}</span>
                    <span>{formatDateTime(a.published_at)}</span>
                  </div>
                  <div className="action-btns">
                    <button className="btn btn-sm btn-ghost" onClick={() => setViewModal(a)}><MdVisibility /> View</button>
                    {(admin?.role !== 'reporter' || a.status === 'draft') && <button className="btn btn-sm btn-ghost" onClick={() => openEdit(a)}><MdEdit /> Edit</button>}
                    <button className="btn btn-sm btn-danger" onClick={() => setDeleteModal(a)}><MdDelete /> Delete</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!isTopEarning && (
            <Pagination
              pagination={pagination}
              onPageChange={setPage}
              limit={limit}
              onLimitChange={setLimit}
            />
          )}
        </>
      )}

      {/* View Article Modal */}
      {viewModal && (
        <div className="modal-overlay" onClick={() => setViewModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '12px', paddingBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
                <h2 style={{ margin: 0, fontFamily: 'Ramabhadra, serif', fontSize: '28px', fontWeight: '400', color: 'var(--text)', flex: 1 }}>{viewModal.title}</h2>
                <button className="modal-close" onClick={() => setViewModal(null)}><MdClose /></button>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', gap: '20px' }}>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                  <span className="badge badge-category">{viewModal.category?.replace('_', ' ')}</span>
                  <span className={`badge ${isLive(viewModal) ? 'badge-live' : 'badge-expired'}`}>{isLive(viewModal) ? 'Live' : 'Expired'}</span>
                  {(!viewModal.category || viewModal.category.trim() === 'General') && (
                    <span className="badge">{displayState(viewModal.city?.replace('_', ' '))}</span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <div
                    onClick={() => { setViewModal(null); navigate(`/articles/engagement/${viewModal.id}?tab=views`); }}
                    title="Click to view Engagement Details for Views"
                    style={{ padding: '8px 14px', background: '#F0F4FF', borderRadius: '8px', fontSize: '13px', color: '#2563EB', fontWeight: '600', cursor: 'pointer' }}
                  >
                    👁️ {viewModal.view_count}
                  </div>
                  <div
                    onClick={() => { setViewModal(null); navigate(`/articles/engagement/${viewModal.id}?tab=likes`); }}
                    title="Click to view Engagement Details for Likes"
                    style={{ padding: '8px 14px', background: '#FEF3C7', borderRadius: '8px', fontSize: '13px', color: '#B45309', fontWeight: '600', cursor: 'pointer' }}
                  >
                    ❤️ {viewModal.like_count}
                  </div>
                  <div
                    onClick={() => { setViewModal(null); navigate(`/articles/engagement/${viewModal.id}?tab=dislikes`); }}
                    title="Click to view Engagement Details for Dislikes"
                    style={{ padding: '8px 14px', background: '#FEE2E2', borderRadius: '8px', fontSize: '13px', color: '#DC2626', fontWeight: '600', cursor: 'pointer' }}
                  >
                    👎 {viewModal.dislike_count}
                  </div>
                  <div
                    onClick={() => { setViewModal(null); navigate(`/articles/engagement/${viewModal.id}?tab=comments`); }}
                    title="Click to view Engagement Details for Comments"
                    style={{ padding: '8px 14px', background: '#E0E7FF', borderRadius: '8px', fontSize: '13px', color: '#4F46E5', fontWeight: '600', cursor: 'pointer' }}
                  >
                    💬 {viewModal.comment_count}
                  </div>
                  <div
                    onClick={() => { setViewModal(null); navigate(`/articles/engagement/${viewModal.id}?tab=shares`); }}
                    title="Click to view Engagement Details for Shares"
                    style={{ padding: '8px 14px', background: '#ECFDF5', borderRadius: '8px', fontSize: '13px', color: '#059669', fontWeight: '600', cursor: 'pointer' }}
                  >
                    ↗️ {viewModal.share_count || 0}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '20px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                <span>
                  By{' '}
                  {(viewModal.contributor?.id || viewModal.creator?.id) ? (
                    <strong
                      onClick={() => { setViewModal(null); navigate(viewModal.contributor?.id ? `/authors/contributor/${viewModal.contributor.id}` : `/authors/admin/${viewModal.creator.id}`); }}
                      style={{ cursor: 'pointer', textDecoration: 'underline', color: '#0369A1' }}
                      title="View author details"
                    >
                      {viewModal.contributor?.name || viewModal.author_name || viewModal.creator?.name || 'Unknown'}
                    </strong>
                  ) : (
                    <strong>{viewModal.author_name || 'Unknown'}</strong>
                  )}
                </span>
                <span>Published: <strong>{new Date(viewModal.published_at).toLocaleDateString('en-GB')}</strong></span>
              </div>
            </div>
            <div className="modal-body modal-grid-2col">
              <div className="detail-row" style={{ gridColumn: '1 / -1' }}><span className="detail-label">Summary</span><span>{viewModal.summary}</span></div>
              {viewModal.source && (
                <div className="detail-row"><span className="detail-label">Source</span><span>{viewModal.source}</span></div>
              )}
              {viewModal.source_url && (
                <div className="detail-row"><span className="detail-label">Source URL</span><a href={viewModal.source_url} target="_blank" rel="noreferrer" className="detail-link">{viewModal.source_url}</a></div>
              )}
              {viewModal.media_type === 'video' && viewModal.video_url ? (
                <div className="detail-row" style={{ gridColumn: '2 / 3', gridRow: '2 / 5' }}>
                  <span className="detail-label">Video</span>
                  <video
                    src={viewModal.video_url}
                    poster={viewModal.thumbnail_url}
                    controls
                    className="detail-img"
                    style={{ objectFit: 'contain', background: '#000', maxHeight: '400px', width: '100%', marginTop: '8px', borderRadius: '12px' }}
                  />
                </div>
              ) : (
                viewModal.image_url && (
                  <div className="detail-row" style={{ gridColumn: '2 / 3', gridRow: '2 / 5' }}>
                    <span className="detail-label">Image</span>
                    <img src={viewModal.image_url} alt="" className="detail-img" style={{ marginTop: '8px', borderRadius: '12px', maxHeight: '400px', objectFit: 'cover' }} />
                  </div>
                )
              )}
              {viewModal.images && viewModal.images.length > 0 && (
                <div className="detail-row">
                  <span className="detail-label">Gallery</span>
                  <div className="flex-row gap-2 flex-wrap">
                    {viewModal.images.map((img, i) => (
                      <img key={i} src={img.image_url} alt="" className="detail-img" style={{ width: 60, height: 60, objectFit: 'cover', margin: 0, borderRadius: '8px' }} />
                    ))}
                  </div>
                </div>
              )}
              {viewModal.is_breaking && (
                <div style={{ gridColumn: '1 / -1', padding: '10px 14px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '8px', fontSize: '13px' }}>
                  <div style={{ color: '#991B1B', fontWeight: '700', marginBottom: '4px' }}>
                    🚨 Breaking News ({(viewModal.breaking_state === 'zone_only' || (viewModal.is_breaking && viewModal.zone_only)) ? 'Show Only in Zone' : (viewModal.breaking_state === ALL_INDIA_STATE || !viewModal.breaking_state) ? 'All (Telangana + Andhra Pradesh)' : viewModal.breaking_state})
                  </div>
                  {viewModal.custom_notification_title && (
                    <div style={{ color: 'var(--text)' }}>
                      <strong>నోటిఫికేషన్ టైటిల్:</strong> {viewModal.custom_notification_title}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="modal-footer" style={{ padding: '16px 24px', background: 'var(--bg)', borderTop: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <button
                className="btn btn-ghost"
                onClick={() => {
                  const targetId = viewModal.id;
                  setViewModal(null);
                  navigate(`/articles/engagement/${targetId}`);
                }}
                style={{ color: '#DC2626', borderColor: '#DC2626', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                View Full Engagement Analytics ➔
              </button>
              <button className="btn btn-primary" onClick={() => setViewModal(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Article Modal */}
      {editModal && (
        <div className="modal-overlay" onClick={() => setEditModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '12px', paddingBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '600', color: 'var(--text)' }}>Edit Article</h2>
                <button className="modal-close" onClick={() => setEditModal(null)}><MdClose /></button>
              </div>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <button
                  type="button"
                  className="btn btn-ghost"
                  style={{ fontSize: '14px', padding: '8px 16px' }}
                  onClick={() => setShowEditWebPreview(true)}
                >
                  👁️ Preview
                </button>
              </div>
            </div>
            <div className="modal-body" style={{ padding: '24px' }}>
              <form className="modal-form" onSubmit={handleEditSubmit}>
                {/* TITLE & SUMMARY */}
                <div style={{ marginBottom: '20px' }}>
                  <div className="form-group">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                      <label style={{ fontSize: '13px', fontWeight: '500', color: 'var(--text)' }}>Title</label>
                      <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{(editForm.title || '').length}/75</span>
                    </div>
                    <input value={editForm.title} onChange={e => setEditForm(f => ({ ...f, title: e.target.value.slice(0, 75) }))} maxLength={75} required style={{ padding: '10px 12px', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '14px', width: '100%', fontFamily: 'inherit', boxSizing: 'border-box' }} />
                  </div>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <div className="form-group">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                      <label style={{ fontSize: '13px', fontWeight: '500', color: 'var(--text)' }}>Summary</label>
                      <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{(editForm.summary || '').length}/400</span>
                    </div>
                    <textarea value={editForm.summary} onChange={e => setEditForm(f => ({ ...f, summary: e.target.value.slice(0, 400) }))} rows={5} maxLength={400} required style={{ padding: '10px 12px', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '14px', fontFamily: 'inherit', resize: 'vertical', width: '100%', boxSizing: 'border-box' }} />
                  </div>
                </div>

                {/* MEDIA TYPE */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ fontSize: '13px', fontWeight: '500', color: 'var(--text)', marginBottom: '8px', display: 'block' }}>Media Type</label>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <label style={{ flex: 1, padding: '10px', border: editForm.media_type === 'image' ? '1.5px solid #2563EB' : '1px solid #E5E7EB', borderRadius: '8px', textAlign: 'center', cursor: 'pointer', backgroundColor: editForm.media_type === 'image' ? '#F0F4FF' : '#FFFFFF', fontSize: '14px', fontWeight: '500', color: editForm.media_type === 'image' ? '#2563EB' : '#6B7280' }}>
                      <input type="radio" value="image" checked={editForm.media_type === 'image'} onChange={() => setEditForm(f => ({ ...f, media_type: 'image' }))} style={{ marginRight: '6px', accentColor: '#2563EB' }} />
                      Image
                    </label>
                    <label style={{ flex: 1, padding: '10px', border: editForm.media_type === 'video' ? '1.5px solid #2563EB' : '1px solid #E5E7EB', borderRadius: '8px', textAlign: 'center', cursor: 'pointer', backgroundColor: editForm.media_type === 'video' ? '#F0F4FF' : '#FFFFFF', fontSize: '14px', fontWeight: '500', color: editForm.media_type === 'video' ? '#2563EB' : '#6B7280' }}>
                      <input type="radio" value="video" checked={editForm.media_type === 'video'} onChange={() => setEditForm(f => ({ ...f, media_type: 'video' }))} style={{ marginRight: '6px', accentColor: '#2563EB' }} />
                      Video
                    </label>
                  </div>
                </div>

                {/* CATEGORY / SUB CATEGORY / CITY — Sub Category & City only apply to General; every other category is location-agnostic */}
                <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
                  <div className="location-picker">
                    <div className="location-picker-row">
                      <span className="location-picker-label">Category</span>
                      <div className="pill-radio-row">
                        {categories.map(cat => {
                          const isSelected = (editForm.category || '').trim() === cat.name.trim();
                          return (
                            <label key={cat.id} className={`pill-radio${isSelected ? ' selected' : ''}`}>
                              <input
                                type="radio"
                                name="edit_quick_category"
                                checked={isSelected}
                                onChange={() => {
                                  setEditForm(f => ({
                                    ...f,
                                    category: cat.name,
                                    ...(cat.name.trim() !== 'General' ? { city: ALL_INDIA_STATE, state: 'all_india' } : {})
                                  }));
                                  if (cat.name.trim() !== 'General') return;
                                  let targetState = cat.name === 'తెలంగాణ' || cat.name === 'ఆంధ్రప్రదేశ్' ? cat.name : ALL_INDIA_STATE + ' ';
                                  setEditState(targetState);
                                  const filtered = cities.filter(c => c.state && c.state.trim() === targetState.trim());
                                  setEditCities(filtered);
                                  if (filtered.length > 0) setEditForm(f => ({ ...f, city: filtered[0].name }));
                                }}
                              />
                              {cat.name}
                            </label>
                          );
                        })}
                      </div>
                    </div>

                    {(editForm.category || '').trim() === 'General' && (
                      <>
                        <div className="location-picker-row">
                          <span className="location-picker-label">Sub Category</span>
                          <div className="pill-radio-row">
                            {['ఆంధ్రప్రదేశ్', 'తెలంగాణ', ALL_INDIA_STATE + ' ', INTERNATIONAL_STATE].map(stateName => {
                              const isSelected = editState && statesMatch(editState, stateName);
                              return (
                                <label key={stateName} className={`pill-radio${isSelected ? ' selected' : ''}`}>
                                  <input
                                    type="radio"
                                    name="edit_quick_state"
                                    checked={isSelected}
                                    onChange={(e) => { setEditState(stateName); const filtered = cities.filter(c => c.state && statesMatch(c.state, stateName)); setEditCities(filtered); if (filtered.length > 0) setEditForm(f => ({ ...f, city: filtered[0].name })); }}
                                  />
                                  {stateName.trim()}
                                </label>
                              );
                            })}
                          </div>
                        </div>

                        <div className="location-picker-row">
                          <span className="location-picker-label">City</span>
                          <SearchableSelect label="" value={editForm.city} options={editCities} onChange={(val) => setEditForm(f => ({ ...f, city: val }))} />
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* STATUS & FEATURED */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                  <div>
                    <label style={{ fontSize: '13px', fontWeight: '500', color: 'var(--text)', marginBottom: '6px', display: 'block' }}>Status</label>
                    {(editForm.status === 'ai_check_pending' || editForm.status === 'ai_check_flagged') ? (
                      <div style={{ padding: '8px 10px', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '13px', color: 'var(--text-secondary)', background: 'var(--bg)' }}>
                        {editForm.status === 'ai_check_pending' ? 'AI check running…' : 'Flagged — use "Possible Duplicate" on the list to decide'}
                      </div>
                    ) : (
                      <select value={editForm.status} onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))} disabled={admin?.role === 'reporter'} style={{ padding: '8px 10px', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '13px', width: '100%', boxSizing: 'border-box' }}>
                        <option value="draft">Draft</option>
                        <option value="published">Published</option>
                      </select>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: '8px', backgroundColor: 'var(--bg)' }}>
                    <input type="checkbox" checked={editForm.is_featured} onChange={(e) => setEditForm(f => ({ ...f, is_featured: e.target.checked }))} style={{ accentColor: '#DC2626', cursor: 'pointer' }} />
                    <label style={{ fontSize: '13px', fontWeight: '500', color: 'var(--text)', cursor: 'pointer', margin: 0 }}>Featured</label>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: '8px', backgroundColor: 'var(--bg)' }}>
                    <input type="checkbox" checked={editForm.zone_only} onChange={(e) => setEditForm(f => ({ ...f, zone_only: e.target.checked }))} style={{ accentColor: '#0369A1', cursor: 'pointer' }} />
                    <label style={{ fontSize: '13px', fontWeight: '500', color: 'var(--text)', cursor: 'pointer', margin: 0 }}>Show only in zone</label>
                  </div>
                </div>

                <div style={{ marginBottom: '20px', gridColumn: '1 / -1' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px', border: '1px solid #FECACA', borderRadius: '8px', backgroundColor: '#FEF2F2' }}>
                    <input
                      type="checkbox"
                      id="articles_edit_is_breaking"
                      checked={editForm.is_breaking}
                      onChange={(e) => setEditForm(f => ({ ...f, is_breaking: e.target.checked }))}
                      style={{ accentColor: '#DC2626', cursor: 'pointer', width: 16, height: 16 }}
                    />
                    <label htmlFor="articles_edit_is_breaking" style={{ fontSize: '14px', fontWeight: '700', color: '#991B1B', cursor: 'pointer', margin: 0 }}>
                      🚨 Breaking News
                    </label>
                  </div>

                  {editForm.is_breaking && (
                    <div style={{ marginTop: '10px', padding: '16px 20px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '12px' }}>
                      <div style={{ marginBottom: '16px' }}>
                        <label style={{ display: 'block', fontSize: '14px', fontWeight: '700', color: '#991B1B', marginBottom: '10px' }}>
                          Show Breaking News For
                        </label>
                        <div className="pill-radio-row">
                          {[
                            { key: 'తెలంగాణ', label: 'తెలంగాణ' },
                            { key: 'ఆంధ్రప్రదేశ్', label: 'ఆంధ్రప్రదేశ్' },
                            { key: ALL_INDIA_STATE, label: 'All (Telangana + Andhra Pradesh)' },
                            { key: 'zone_only', label: 'Show Only in Zone' }
                          ].map((option) => {
                            const isSelected = (editForm.breaking_state || ALL_INDIA_STATE).trim() === option.key.trim();
                            return (
                              <label key={option.key} className={`pill-radio${isSelected ? ' selected' : ''}`} style={{ fontSize: '15px', padding: '10px 18px' }}>
                                <input
                                  type="radio"
                                  name="articles_edit_breaking_state"
                                  value={option.key}
                                  checked={isSelected}
                                  onChange={() => setEditForm(f => ({
                                    ...f,
                                    breaking_state: option.key,
                                    ...(option.key === 'zone_only' ? { zone_only: true } : {})
                                  }))}
                                />
                                {option.label}
                              </label>
                            );
                          })}
                        </div>
                      </div>

                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                          <label style={{ fontSize: '13px', fontWeight: '600', color: '#991B1B' }}>
                            Custom Notification Title (Optional)
                          </label>
                          <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                            Leave blank to use original article title
                          </span>
                        </div>
                        <input
                          type="text"
                          value={editForm.custom_notification_title || ''}
                          onChange={(e) => setEditForm(f => ({ ...f, custom_notification_title: e.target.value }))}
                          placeholder="Example: Heavy Rain Alert in Hyderabad"
                          style={{ fontSize: '14px', padding: '10px 14px', borderColor: '#FCA5A5', width: '100%', borderRadius: '8px', border: '1.5px solid #FCA5A5', boxSizing: 'border-box' }}
                        />
                      </div>
                    </div>
                  )}
                </div>


                {/* MEDIA SECTION - FULL WIDTH */}
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ fontSize: '13px', fontWeight: '500', color: 'var(--text)', marginBottom: '12px', display: 'block' }}>Media</label>
                  {editForm.media_type === 'image' && (
                    <>
                      <div className="upload-area" tabIndex={0} onClick={() => handleEditFileSelect('image')} onPaste={handleEditPasteImage} style={{ minHeight: '220px', borderRadius: '10px', border: '2px dashed #E5E7EB', cursor: 'pointer' }}>
                        {editForm.image_url ? (
                          <img src={editForm.image_url} alt="preview" className="upload-preview" />
                        ) : (
                          <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>{editUploading ? 'Processing...' : 'Click to upload image'}</p>
                        )}
                      </div>

                      {/* Gallery Images Section */}
                      <div className="form-group" style={{ marginTop: '20px' }} tabIndex={0} onPaste={handleEditGalleryPasteImage}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                          <label style={{ margin: 0, fontWeight: 700, fontSize: '13px', color: 'var(--text)' }}>Gallery Images</label>
                          <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Click, drag & drop, or paste (Ctrl+V)</span>
                        </div>
                        <div
                          className="upload-area"
                          tabIndex={0}
                          onPaste={handleEditGalleryPasteImage}
                          onClick={() => {
                            const input = document.createElement('input');
                            input.type = 'file';
                            input.multiple = true;
                            input.accept = 'image/jpeg,image/jpg,image/png,image/webp';
                            input.onchange = (e) => handleEditGalleryFilesUpload(Array.from(e.target.files));
                            input.click();
                          }}
                          style={{ cursor: 'pointer', padding: '24px 16px', border: '2px dashed #CBD5E1', borderRadius: '10px', textAlign: 'center', backgroundColor: 'var(--bg)' }}
                        >
                          <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-secondary)', fontWeight: 500 }}>
                            {editGalleryUploading ? 'Uploading gallery images...' : 'Click to select multiple images, drag & drop, or paste (Ctrl+V)'}
                          </p>
                        </div>

                        {/* Gallery Thumbnails Grid & Plus Card */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: '12px', marginTop: '16px' }}>
                          {(editForm.images || []).map((imgUrl, index) => (
                            <div key={index} style={{ position: 'relative', borderRadius: '8px', border: '1px solid var(--border)', overflow: 'hidden', backgroundColor: 'var(--card)', height: '108px', display: 'flex', flexDirection: 'column' }}>
                              <img src={typeof imgUrl === 'string' ? imgUrl : imgUrl.image_url || imgUrl.url} alt={`gallery-${index}`} style={{ width: '100%', height: '80px', objectFit: 'cover', display: 'block' }} />
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 6px', background: 'var(--bg)', borderTop: '1px solid #E2E8F0', height: '28px', boxSizing: 'border-box' }}>
                                <div style={{ display: 'flex', gap: '2px' }}>
                                  <button
                                    type="button"
                                    disabled={index === 0}
                                    onClick={(e) => { e.stopPropagation(); moveEditGalleryImage(index, -1); }}
                                    style={{ border: 'none', background: 'transparent', cursor: index === 0 ? 'default' : 'pointer', opacity: index === 0 ? 0.3 : 1, fontSize: '12px', padding: '2px 4px' }}
                                    title="Move Left"
                                  >
                                    ◀
                                  </button>
                                  <button
                                    type="button"
                                    disabled={index === (editForm.images || []).length - 1}
                                    onClick={(e) => { e.stopPropagation(); moveEditGalleryImage(index, 1); }}
                                    style={{ border: 'none', background: 'transparent', cursor: index === (editForm.images || []).length - 1 ? 'default' : 'pointer', opacity: index === (editForm.images || []).length - 1 ? 0.3 : 1, fontSize: '12px', padding: '2px 4px' }}
                                    title="Move Right"
                                  >
                                    ▶
                                  </button>
                                </div>
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); removeEditGalleryImage(index); }}
                                  style={{ border: 'none', background: 'transparent', color: '#EF233C', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold', padding: '2px 4px' }}
                                  title="Remove Image"
                                >
                                  ✕
                                </button>
                              </div>
                            </div>
                          ))}

                          {/* Add Images (+) Card */}
                          <div
                            onClick={() => {
                              const input = document.createElement('input');
                              input.type = 'file';
                              input.multiple = true;
                              input.accept = 'image/jpeg,image/jpg,image/png,image/webp';
                              input.onchange = (e) => handleEditGalleryFilesUpload(Array.from(e.target.files));
                              input.click();
                            }}
                            style={{
                              height: '108px',
                              border: '2px dashed #CBD5E1',
                              borderRadius: '8px',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              backgroundColor: 'var(--bg)',
                              transition: 'all 0.2s ease',
                              color: 'var(--text-secondary)',
                              boxSizing: 'border-box'
                            }}
                            title="Add Images"
                          >
                            <span style={{ fontSize: '24px', fontWeight: 'bold', lineHeight: 1 }}>+</span>
                            <span style={{ fontSize: '12px', fontWeight: 600, marginTop: '4px' }}>Add Images</span>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                  {editForm.media_type === 'video' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      {/* Video Player & Upload / Replace Controls */}
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                          <label style={{ fontSize: '13px', fontWeight: '500', color: 'var(--text)', margin: 0 }}>Article Video</label>
                          {editForm.video_url && (
                            <span style={{ fontSize: '12px', color: '#16A34A', fontWeight: '500' }}>✓ Video Attached</span>
                          )}
                        </div>

                        {editForm.video_url ? (
                          <div style={{ borderRadius: '10px', overflow: 'hidden', border: '1px solid var(--border)', backgroundColor: '#000' }}>
                            <video
                              src={editForm.video_url}
                              poster={editForm.thumbnail_url || undefined}
                              controls
                              style={{ width: '100%', maxHeight: '260px', display: 'block', objectFit: 'contain' }}
                            />
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: 'var(--bg)', borderTop: '1px solid #E5E7EB' }}>
                              <button
                                type="button"
                                className="btn btn-sm btn-ghost"
                                onClick={() => handleEditFileSelect('video')}
                                disabled={editUploading}
                                style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}
                              >
                                {editUploading ? 'Uploading video...' : '🔄 Replace Video'}
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-danger"
                                onClick={() => setEditForm(f => ({ ...f, video_url: '' }))}
                                style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}
                              >
                                🗑️ Remove Video
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div
                            className="upload-area"
                            onClick={() => handleEditFileSelect('video')}
                            style={{ minHeight: '140px', borderRadius: '10px', border: '2px dashed #CBD5E1', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--bg)', padding: '20px' }}
                          >
                            <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-secondary)', fontWeight: 500 }}>
                              {editUploading ? 'Uploading video...' : '🎬 Click to upload video file (.mp4)'}
                            </p>
                            <span style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>Supports MP4 video files</span>
                          </div>
                        )}
                      </div>

                      {/* Video URL Input (Alternative/Direct Paste) */}
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px', display: 'block' }}>Or paste Video URL (MP4)</label>
                        <input
                          type="text"
                          value={editForm.video_url}
                          onChange={e => setEditForm(f => ({ ...f, video_url: e.target.value }))}
                          placeholder="https://..."
                          style={{ padding: '8px 12px', border: '1px solid var(--border)', borderRadius: '8px', width: '100%', fontSize: '13px', fontFamily: 'inherit', boxSizing: 'border-box' }}
                        />
                      </div>

                      {/* Video Thumbnail Section */}
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label style={{ fontSize: '13px', fontWeight: '500', color: 'var(--text)', marginBottom: '6px', display: 'block' }}>Video Thumbnail (App & Web)</label>
                        <div
                          className="upload-area"
                          onClick={() => handleEditFileSelect('thumbnail')}
                          style={{ minHeight: '160px', borderRadius: '10px', border: '2px dashed #E5E7EB', cursor: 'pointer', backgroundColor: 'var(--bg)' }}
                        >
                          {editForm.thumbnail_url ? (
                            <img src={editForm.thumbnail_url} alt="thumbnail preview" className="upload-preview" />
                          ) : (
                            <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
                              {editUploading ? 'Processing...' : 'Click to upload thumbnail image'}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* ADDITIONAL FIELDS */}
                <div style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px 32px' }}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label style={{ fontSize: '13px', fontWeight: '500', color: 'var(--text)', margin: 0, marginBottom: '6px', display: 'block' }}>Source</label>
                    <input type="text" value={editForm.source} onChange={e => setEditForm(f => ({ ...f, source: e.target.value }))} placeholder="e.g. Times of India..." style={{ padding: '10px 12px', border: '1.5px solid var(--border)', borderRadius: '10px', width: '100%', fontSize: '14px', fontFamily: 'inherit', boxSizing: 'border-box' }} />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label style={{ fontSize: '13px', fontWeight: '500', color: 'var(--text)', margin: 0, marginBottom: '6px', display: 'block' }}>Source URL</label>
                    <input type="url" value={editForm.source_url} onChange={e => setEditForm(f => ({ ...f, source_url: e.target.value }))} placeholder="https://..." style={{ padding: '10px 12px', border: '1.5px solid var(--border)', borderRadius: '10px', width: '100%', fontSize: '14px', fontFamily: 'inherit', boxSizing: 'border-box' }} />
                  </div>
                </div>
              </form>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setEditModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveEdit}>Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal && (
        <div className="modal-overlay" onClick={() => setDeleteModal(null)}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete Article</h3>
              <button className="modal-close" onClick={() => setDeleteModal(null)}><MdClose /></button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this article?</p>
              <p className="text-muted" style={{ marginTop: 8 }}>"{deleteModal.title}"</p>
              <p className="text-muted" style={{ marginTop: 8 }}>This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setDeleteModal(null)}>Cancel</button>
              <button className="btn btn-danger-solid" onClick={confirmDelete} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}


      {showEditWebPreview && (
        <WebPreviewModal
          article={editForm}
          onClose={() => setShowEditWebPreview(false)}
        />
      )}

      {editCropper.active && (
        <ImageCropper
          image={editCropper.image}
          onCropComplete={handleEditCropComplete}
          onCancel={() => setEditCropper({ active: false, image: null, type: null })}
        />
      )}

      {duplicateModal && (
        <DuplicateMatchesModal
          articleId={duplicateModal}
          onClose={() => setDuplicateModal(null)}
          onResolved={() => { setDuplicateModal(null); fetchArticles(); }}
        />
      )}
    </div>
  );
}
